import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import { type Field, match as matchField } from "foldkit/fieldValidation";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";
import { Calendar } from "lucide";

import { imageBase } from "../../api/client";
import { type EntryType, RecordType } from "../../api/schemas/common";
import type { OpenTask, Week } from "../../api/schemas/keepInTouch";
import type { RecordSummary } from "../../api/schemas/records";
import { Route, linkHref } from "../../app/routes";
import {
	ENTRY_SUBTYPE_GROUPS,
	entrySubtypeLabel,
	i18next,
	recordSubtypeLabel,
} from "../../i18n";
import * as AddEntry from "../../widgets/addEntry";
import * as Attributes from "../../widgets/attributes";
import { Dialog, confirmDialog } from "../../widgets/dialog";
import {
	AlertCircle,
	Download,
	FileText,
	Plus,
	Search,
	icon,
} from "../../widgets/icons";
import * as Cards from "../../widgets/keepInTouchCards";
import * as Picker from "../../widgets/recordPicker";
import * as Tags from "../../widgets/tags";
import * as ValidatedField from "../../widgets/validatedField";
import { tagSuggestions } from "../entry/update";
import type { Shared } from "../shared";
import { Message } from "./message";
import { EntryFilter, type Model, type Row, SearchType } from "./model";
import { parseSearchInput } from "./searchInput";
import {
	SEARCH_INPUT_ID,
	isEmptyCircle,
	isFeed,
	isFeedSettled,
	recordSchema,
	recordToRow,
	rows,
	visibleRows,
} from "./update";

const t = (key: string, options: Record<string, unknown> = {}): string =>
	i18next.t(key, options);

/** The row's subtype label: a record's type, or an entry's, when the row carries one. */
const subtypeLabel = (row: Row): string | undefined =>
	row.type === "node" && row.nodeType
		? recordSubtypeLabel(row.nodeType)
		: row.type === "knot" && row.knotType
			? entrySubtypeLabel(row.knotType)
			: undefined;

const recordLink = (current: Route, id: string): string =>
	linkHref(current, Route.Record({ id, asOf: Option.none() }));

export const rowHref = (current: Route, row: Row): string =>
	row.type === "node"
		? recordLink(current, row.id)
		: row.type === "knot"
			? linkHref(current, Route.Entry({ id: row.id, asOf: Option.none() }))
			: `/events/${row.id}`;

/** v1's dynamic confirm text, with the per-type breakdown. */
export const deleteMessage = (selected: ReadonlyArray<Row>): string => {
	const count = selected.length;
	const records = selected.filter((r) => r.type === "node").length;
	const entries = selected.filter((r) => r.type === "knot").length;
	const events = selected.filter((r) => r.type === "event").length;
	const parts = [
		...(records > 0
			? [t("search.deleteConfirm.records", { count: records })]
			: []),
		...(entries > 0
			? [t("search.deleteConfirm.entries", { count: entries })]
			: []),
		...(events > 0
			? [t("search.deleteConfirm.events", { count: events })]
			: []),
	];
	return `${t("search.deleteConfirm.question", { count })}${
		parts.length > 0 ? ` (${parts.join(", ")})` : ""
	}`;
};

const link = (h: HtmlBuilder<Message>, shared: Shared, row: Row) =>
	h.Href(rowHref(shared.route, row));

/** v1 `ListSearchResultView` avatar column (`avatar-group` or a 1rem spacer). */
const avatar = (h: HtmlBuilder<Message>, shared: Shared, row: Row): Html => {
	if (row.type !== "node")
		return h.div([h.Class("flex-shrink-0 w-4"), h.AriaHidden(true)], []);
	const ring =
		"w-8 rounded-full ring-3 ring-primary ring-offset-base-100 ring-offset-1";
	const profile =
		row.avatar?.exists && row.avatar.imageUrl
			? `${imageBase()}${row.avatar.imageUrl}`
			: null;
	const urls = (row.avatars ?? []).filter((url) => url.length > 0);
	return h.div(
		[h.Class("avatar-group -space-x-6")],
		profile
			? [
					h.div(
						[h.Class("avatar")],
						[
							h.div(
								[h.Class(ring)],
								[
									h.img([
										h.Src(profile),
										h.Alt(`${row.title}'s profile`),
										h.Class("object-cover"),
									]),
								],
							),
						],
					),
				]
			: urls.length > 0
				? urls.map((url, index) =>
						h.div(
							[
								h.Key(url),
								h.Class("avatar"),
								h.Style({ zIndex: String(urls.length - index) }),
							],
							[
								h.div(
									[h.Class(ring)],
									[
										h.img([
											h.Src(url),
											h.Alt(`${row.title}'s avatar`),
											h.Class("object-cover"),
										]),
									],
								),
							],
						),
					)
				: [
						h.div(
							[h.Class("avatar avatar-placeholder")],
							[
								h.div(
									[h.Class(`bg-neutral text-neutral-content ${ring}`)],
									[
										h.span(
											[],
											[
												h.a(
													[link(h, shared, row)],
													[[...row.title][0]?.toUpperCase() || "?"],
												),
											],
										),
									],
								),
							],
						),
					],
	);
};

/** One "label: value" fragment on a result row, shared by highlights and foundInfo. */
const fieldRow = (
	h: HtmlBuilder<Message>,
	label: string,
	value: ReadonlyArray<Html | string>,
): Html =>
	h.span(
		[h.Class("mr-2")],
		[h.span([h.Class("font-medium capitalize")], [`${label}:`]), " ", ...value],
	);

const resultRow = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	row: Row,
	selected: boolean,
): Html => {
	const matches = row.matches ?? [];
	return h.keyed("div")(
		row.id,
		[h.Class("bg-base-100 border p-1 rounded-xs flex items-center")],
		[
			h.div(
				[h.Class("flex items-center")],
				[
					h.input([
						h.Type("checkbox"),
						h.Class("checkbox checkbox-sm mr-2 ml-2"),
						h.AriaLabel(t("search.selectRow", { title: row.title })),
						h.Checked(selected),
						h.OnClick(
							Message.ToggledSelect({ id: row.id, selected: !selected }),
						),
					]),
					h.div(
						[h.Class("flex items-center gap-2")],
						[
							avatar(h, shared, row),
							h.div(
								[],
								[
									h.h2(
										[h.Class("text-lg font-semibold")],
										[
											h.a(
												[
													link(h, shared, row),
													h.Class("hover:text-primary transition-colors"),
												],
												[row.title],
											),
											subtypeLabel(row) === undefined
												? h.empty
												: h.span(
														[
															h.Class(
																"badge badge-outline badge-sm ml-2 align-middle",
															),
														],
														[subtypeLabel(row) ?? ""],
													),
										],
									),
									matches.length > 0
										? h.p(
												[h.Class("text-sm")],
												matches.map((match) =>
													fieldRow(
														h,
														match.field,
														// Segment text is a text node; highlights come from the payload, not the input.
														match.segments.map((segment) =>
															segment.matched
																? h.mark(
																		[h.Class("bg-warning/40 rounded-xs")],
																		[segment.text],
																	)
																: segment.text,
														),
													),
												),
											)
										: row.foundInfo.length === 0
											? h.p([h.Class("text-sm")], [row.subtitle])
											: h.p(
													[h.Class("text-sm")],
													row.foundInfo.map((info) =>
														fieldRow(h, info.key, [h.span([], [info.value])]),
													),
												),
								],
							),
						],
					),
				],
			),
			row.type === "node"
				? h.empty
				: h.div(
						[h.Class("ml-auto flex-shrink-0"), h.Title(row.type)],
						[
							row.type === "event"
								? icon(h, "Calendar", Calendar, {
										class: "w-4 h-4 text-base-content/60",
									})
								: FileText(h, { class: "w-4 h-4 text-base-content/60" }),
						],
					),
		],
	);
};

/** v1 `RecordProfileEditForm` Name: a `form-control` label with the error in a second `label` row. */
const nameField = (h: HtmlBuilder<Message>, field: Field<string>): Html =>
	h.label(
		[h.Class("form-control w-full")],
		[
			h.div(
				[h.Class("label")],
				[
					h.span(
						[h.Class("label-text font-semibold")],
						[t("search.form.name")],
					),
				],
			),
			h.input([
				h.Type("text"),
				h.Placeholder(""),
				h.AriaLabel(t("search.form.name")),
				h.Maxlength(ValidatedField.RECORD_FIELD_MAX.name),
				h.Class(
					`input input-bordered ${ValidatedField.isInvalid(field) ? "input-error" : ""} w-full`,
				),
				h.Value(field.value),
				h.OnInput((value) => Message.ChangedName({ value })),
			]),
			h.div(
				[h.Class("label")],
				[
					matchField(field, {
						onNotValidated: () => h.empty,
						onValidating: () => h.empty,
						onValid: () => h.empty,
						onInvalid: ({ errors }) =>
							h.span([h.Class("label-text text-error")], [errors.join("; ")]),
					}),
				],
			),
		],
	);

const addRecordDialog = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
): Html =>
	h.submodel({
		slotId: "add-record",
		view: Dialog.view,
		model: model.addDialog,
		toParentMessage: (message: Dialog.Message) =>
			Message.GotAddDialogMessage({ message }),
		viewInputs: {
			toView: (render) =>
				h.dialog(
					[...render.dialog, h.Class("modal")],
					render.isVisible
						? [
								h.div([...render.backdrop, h.Class("modal-backdrop")], []),
								h.div(
									[...render.panel, h.Class("modal-box rounded-md max-w-4xl")],
									[
										h.button(
											[
												...render.closeButton,
												h.Class(
													"btn btn-sm btn-circle btn-ghost absolute right-2 top-2",
												),
												h.AriaLabel(t("dialog.addRecord.close")),
											],
											["✕"],
										),
										h.h3(
											[...render.title, h.Class("font-bold text-lg mb-4")],
											[t("dialog.addRecord.title")],
										),
										Option.match(model.form.error, {
											onNone: () => h.empty,
											onSome: (error) =>
												h.div(
													[h.Role("alert"), h.Class("alert alert-error mb-4")],
													[
														h.span([], [error]),
														h.button(
															[
																h.Type("button"),
																h.Class("btn btn-sm btn-ghost ml-auto"),
																h.AriaLabel(t("dialog.addRecord.dismissError")),
																h.OnClick(Message.ClickedDismissError()),
															],
															["✕"],
														),
													],
												),
										}),
										h.form(
											[
												h.AriaLabel(t("search.addRecordForm")),
												h.Class("flex flex-col gap-4"),
												h.OnSubmit(Message.SubmittedAddRecord()),
											],
											[
												h.div(
													[h.Class("flex flex-col md:flex-row gap-8")],
													[
														h.div(
															[
																h.Class(
																	"md:w-1/3 flex flex-col items-center gap-3",
																),
															],
															[
																h.div(
																	[
																		h.Class(
																			"w-48 h-48 bg-base-300 rounded-lg flex items-center justify-center",
																		),
																	],
																	[
																		h.span(
																			[h.Class("text-base-content/60")],
																			[t("search.form.noImage")],
																		),
																	],
																),
																h.p(
																	[
																		h.Class(
																			"text-sm text-base-content/60 text-center",
																		),
																	],
																	[t("search.form.imageLater")],
																),
															],
														),
														h.div(
															[h.Class("md:w-2/3 flex flex-col gap-4")],
															[
																nameField(h, model.form.name),
																h.div(
																	[],
																	[
																		h.label(
																			[
																				h.For("add-type"),
																				h.Class("block font-semibold"),
																			],
																			[t("search.type")],
																		),
																		h.select(
																			[
																				h.Id("add-type"),
																				h.Class(
																					"select select-bordered w-full",
																				),
																				h.Value(model.form.type),
																				h.OnChange((value) =>
																					Message.ChangedRecordType({
																						value:
																							RecordType.literals.find(
																								(t) => t === value,
																							) ?? "entity",
																					}),
																				),
																			],
																			(
																				[
																					"entity",
																					"person",
																					"group",
																					"effort",
																				] as const
																			).map((type) =>
																				h.option(
																					[
																						h.Value(type),
																						h.Selected(
																							model.form.type === type,
																						),
																					],
																					[recordSubtypeLabel(type)],
																				),
																			),
																		),
																	],
																),
																ValidatedField.textInput(
																	h,
																	"add-subtitle",
																	t("search.form.subtitle"),
																	model.form.subtitle,
																	(value) => Message.ChangedSubtitle({ value }),
																	"text",
																	undefined,
																	ValidatedField.RECORD_FIELD_MAX.subtitle,
																),
																ValidatedField.textInput(
																	h,
																	"add-email",
																	t("search.form.email"),
																	model.form.email,
																	(value) => Message.ChangedEmail({ value }),
																	"email",
																	"example@email.com",
																	ValidatedField.RECORD_FIELD_MAX.email,
																),
																h.div(
																	[],
																	[
																		h.label(
																			[h.Class("block font-semibold")],
																			[t("search.form.customAttributes")],
																		),
																		Attributes.view(h, {
																			rows: model.form.attributes,
																			schema: recordSchema(shared),
																			toParentMessage: (message) =>
																				Message.GotAttributesMessage({
																					message,
																				}),
																		}),
																	],
																),
																h.div(
																	[],
																	[
																		h.label(
																			[
																				h.For("add-description"),
																				h.Class("block font-semibold"),
																			],
																			[t("search.form.description")],
																		),
																		h.textarea([
																			h.Id("add-description"),
																			h.Maxlength(
																				ValidatedField.RECORD_FIELD_MAX
																					.description,
																			),
																			h.Class(
																				`textarea textarea-bordered w-full min-h-24 ${ValidatedField.isInvalid(model.form.description) ? "textarea-error" : ""}`,
																			),
																			h.Value(model.form.description.value),
																			h.OnInput((value) =>
																				Message.ChangedDescription({ value }),
																			),
																		]),
																		ValidatedField.error(
																			h,
																			model.form.description,
																		),
																	],
																),
																h.div(
																	[],
																	[
																		h.label(
																			[
																				h.For("add-record-tags"),
																				h.Class("block font-semibold"),
																			],
																			[t("search.form.tags")],
																		),
																		Tags.view(h, {
																			slotId: "add-record-tags",
																			model: model.form.tagBox,
																			selected: model.form.tags,
																			suggestions: tagSuggestions(shared),
																			ariaLabel: t("search.form.tags"),
																			placeholder: t(
																				"search.form.tagPlaceholder",
																			),
																			error: model.form.tagError,
																			toParentMessage: (message) =>
																				Message.GotTagsMessage({ message }),
																			onRemove: (tag) =>
																				Message.ClickedRemoveTag({ tag }),
																		}),
																	],
																),
															],
														),
													],
												),
												h.div(
													[h.Class("flex justify-end mt-4")],
													[
														h.button(
															[
																h.Type("submit"),
																h.Class("btn btn-primary"),
																h.AriaLabel(t("search.form.create")),
																h.Disabled(model.saving),
															],
															[
																model.saving
																	? h.span(
																			[h.Class("loading loading-spinner")],
																			[],
																		)
																	: h.empty,
																t("search.form.create"),
															],
														),
													],
												),
											],
										),
									],
								),
							]
						: [],
				),
		},
	});

/** v1 `LabeledBtn` (icon button with the label under it). */
const labeledBtn = (
	h: HtmlBuilder<Message>,
	iconHtml: Html,
	label: string,
	onClick: Message,
): Html =>
	h.div(
		[h.Class("flex flex-col items-center mr-2")],
		[
			h.button(
				[
					h.Type("button"),
					h.Class("btn mb-1  "),
					h.AriaLabel(label),
					h.OnClick(onClick),
				],
				[iconHtml],
			),
			h.span([h.Class("")], [label]),
		],
	);

const emptyState = (h: HtmlBuilder<Message>, model: Model): Html => {
	const { text, tags } = parseSearchInput(model.searchText);
	const hasText = text.trim().length > 0;
	const hasTags = tags.length > 0;
	const prompt = (lines: ReadonlyArray<string>) =>
		h.div(
			[h.Class("text-center text-base-content/60 mt-8")],
			[
				Search(h, { size: 48, class: "mx-auto mb-4 opacity-50" }),
				...lines.map((line, i) =>
					h.p(i === 0 ? [] : [h.Class("text-sm mt-2")], [line]),
				),
			],
		);
	if (!hasText && !hasTags) {
		// v1 `ImportExportOptions` (developer-only importers not ported).
		return model.searchType === "all" || model.searchType === "node"
			? h.div(
					[h.Class("text-center text-base-content/60")],
					[
						h.p([], [t("search.noRecords")]),
						h.div(
							[h.Class("mt-4 grid gap-4 grid-cols-1")],
							[
								labeledBtn(
									h,
									Plus(h),
									t("search.manuallyAdd"),
									Message.ClickedAddRecord(),
								),
							],
						),
					],
				)
			: prompt([t("search.enterTerm")]);
	}
	return hasTags
		? prompt([t("search.noTagResults"), t("search.noTagResultsHint")])
		: prompt([
				t("search.noResults", { query: model.searchText }),
				t("search.noResultsHint"),
			]);
};

/** The brief's section title: serif display, with an optional trailing action. */
const sectionHeading = (
	h: HtmlBuilder<Message>,
	title: string,
	action: Html = h.empty,
): Html =>
	h.div(
		[h.Class("flex items-baseline justify-between gap-4")],
		[h.h2([h.Class("font-display text-2xl font-medium")], [title]), action],
	);

/** The page's opening line: how many people are waiting and how far through the week you are. */
const briefHeader = (h: HtmlBuilder<Message>, model: Model): Html => {
	const week = AsyncData.getOrElse(model.keepInTouch, () => null);
	if (week === null || isEmptyCircle(model)) return h.empty;
	const total = week.cards.length;
	const waiting = Cards.visible(week).length;
	return h.header(
		[h.Class("mb-8")],
		[
			h.h1(
				[
					h.Class(
						"font-display text-4xl font-light leading-tight tracking-tight",
					),
				],
				[
					waiting === 0
						? i18next.t("home.brief.title_zero")
						: i18next.t("home.brief.title", { count: waiting }),
				],
			),
			total === 0
				? h.empty
				: h.p(
						[h.Class("mt-2 text-base-content/60")],
						[
							i18next.t("home.brief.reached", {
								done: total - waiting,
								total,
							}),
						],
					),
		],
	);
};

/** KTD8, R17: the week's cards as Home's hero, one per row (R6); R20 empty state; a 409 message above the cards. */
const keepInTouchStrip = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
): Html => {
	const week = AsyncData.getOrElse(model.keepInTouch, () => null);
	if (week === null) {
		return AsyncData.isFailure(model.keepInTouch)
			? h.div(
					[h.Role("alert"), h.Class("alert alert-error alert-sm mb-4")],
					[h.span([], [i18next.t("keepInTouch.error.load")])],
				)
			: h.empty;
	}
	const cards = Cards.visible(week);
	return h.section(
		[
			h.AriaLabel(
				i18next.t("keepInTouch.strip.heading", { count: cards.length }),
			),
			h.Class("mb-4 flex flex-col gap-2"),
		],
		[
			sectionHeading(
				h,
				i18next.t("keepInTouch.strip.heading", { count: cards.length }),
				h.a(
					[
						h.Href(
							linkHref(
								shared.route,
								Route.KeepInTouch({ asOf: Option.none() }),
							),
						),
						h.Class("link link-hover text-sm text-primary"),
					],
					[i18next.t("keepInTouch.strip.seeAll")],
				),
			),
			Option.match(model.keepInTouchError, {
				onNone: () => h.empty,
				onSome: (error) =>
					h.p([h.Role("alert"), h.Class("text-sm text-error")], [error]),
			}),
			week.cards.length === 0
				? Cards.emptyState(h, Message.ClickedAddRecord())
				: cards.length === 0
					? h.p(
							[h.Class("text-sm text-base-content/70")],
							[i18next.t("keepInTouch.strip.allClear")],
						)
					: Cards.view(h, {
							cards,
							current: shared.route,
							layout: "rows",
							toParentMessage: (message) => Message.GotCardMessage({ message }),
						}),
		],
	);
};

/** R8: the picker in front of the shared add-entry widget. */
const captureBox = (h: HtmlBuilder<Message>, model: Model, shared: Shared) =>
	h.section(
		[
			h.DataAttribute("testid", "home-capture"),
			h.AriaLabel(i18next.t("home.capture.heading")),
			h.Class(
				"card card-border bg-base-100 flex flex-col gap-3 p-4 shadow-sm sm:p-5",
			),
		],
		[
			// Home has no toolbar (the shell owns search), so new-record and import sit on the capture card.
			sectionHeading(
				h,
				i18next.t("home.capture.heading"),
				h.div(
					[h.Class("flex gap-1")],
					[
						h.button(
							[
								h.Type("button"),
								h.Class("btn btn-ghost btn-sm btn-square"),
								h.AriaLabel(t("search.addRecord")),
								h.Title(t("search.addRecord")),
								h.OnClick(Message.ClickedAddRecord()),
							],
							[Plus(h)],
						),
						h.a(
							[
								h.Href(
									linkHref(
										shared.route,
										Route.Import({
											asOf: Option.none(),
											onboarding: Option.none(),
										}),
									),
								),
								h.Class("btn btn-ghost btn-sm btn-square"),
								h.AriaLabel(i18next.t("import.title")),
								h.Title(i18next.t("import.title")),
							],
							[Download(h)],
						),
					],
				),
			),
			h.submodel({
				slotId: "home-record-picker",
				view: Picker.view,
				model: model.capture.picker,
				toParentMessage: (message: Picker.Message) =>
					Message.GotPickerMessage({ message }),
				viewInputs: {},
			}),
			// AddEntry renders a timeline <li>; outside a timeline <ul> it gets a list bullet.
			h.ul(
				[
					h.Class(
						"timeline timeline-snap-icon timeline-compact timeline-vertical",
					),
				],
				[
					h.submodel({
						slotId: "home-add-entry",
						view: AddEntry.view,
						model: model.capture.addEntry,
						toParentMessage: (message: AddEntry.Message) =>
							Message.GotAddEntryMessage({ message }),
						viewInputs: {
							hasOtherEntries: false,
							dark: shared.theme === "dark",
							tagSuggestions: tagSuggestions(shared),
						},
					}),
				],
			),
		],
	);

/** R10: the week payload's open tasks, each linking to its record. */
const openTasksSection = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	tasks: ReadonlyArray<OpenTask>,
) => {
	const heading = i18next.t("home.openTasks.heading", { count: tasks.length });
	return h.section(
		[
			h.DataAttribute("testid", "home-open-tasks"),
			h.AriaLabel(heading),
			h.Class("flex flex-col gap-2"),
		],
		[
			sectionHeading(h, heading),
			tasks.length === 0
				? h.p(
						[h.Class("text-sm text-base-content/70")],
						[i18next.t("home.openTasks.empty")],
					)
				: h.ul(
						[h.Class("flex flex-col")],
						tasks.map((task) =>
							h.li(
								[
									h.Key(task.id),
									h.Class(
										"flex items-baseline gap-3 border-t border-base-300 py-2 text-sm last:border-b",
									),
								],
								[
									h.span(
										[
											h.AriaHidden(true),
											h.Class(
												"size-3.5 shrink-0 translate-y-0.5 rounded border border-base-300",
											),
										],
										[],
									),
									h.span([h.Class("grow")], [task.text]),
									h.a(
										[
											h.Href(recordLink(shared.route, task.record.id)),
											h.Class(
												"link link-hover shrink-0 text-xs text-base-content/60",
											),
										],
										[task.record.name],
									),
								],
							),
						),
					),
		],
	);
};

/** R11: avatar and name per record; the caller hides the section when the list is empty. */
const recentSection = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	records: ReadonlyArray<RecordSummary>,
) =>
	h.section(
		[
			h.DataAttribute("testid", "home-recent"),
			h.AriaLabel(i18next.t("home.recent.heading")),
			h.Class("flex flex-col gap-2"),
		],
		[
			sectionHeading(h, i18next.t("home.recent.heading")),
			h.ul(
				[h.Class("flex flex-wrap gap-2")],
				records.map((record) => {
					const row = recordToRow(record);
					return h.li(
						[
							h.Key(record.id),
							h.Class(
								"flex items-center gap-2 rounded-full border border-base-300 py-1 pl-1 pr-3",
							),
						],
						[
							avatar(h, shared, row),
							h.a(
								[
									h.Href(recordLink(shared.route, record.id)),
									h.Class("link link-hover text-sm"),
								],
								[record.name],
							),
						],
					);
				}),
			),
		],
	);

/** The Keep in touch page's tracked and paused lists, collapsed; editing stays on the record page. */
const cadencesSection = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	week: Week,
): Html => {
	const tracked = week.tracked.filter((row) => !row.paused);
	const count = tracked.length + week.paused.length;
	if (count === 0) return h.empty;
	const heading = i18next.t("home.cadences.heading", { count });
	const item = (id: string, name: string, detail: string) =>
		h.li(
			[
				h.Key(id),
				h.Class(
					"flex items-baseline gap-3 border-t border-base-300 py-2 text-sm last:border-b",
				),
			],
			[
				h.a(
					[
						h.Href(recordLink(shared.route, id)),
						h.Class("link link-hover grow"),
					],
					[name],
				),
				h.span([h.Class("shrink-0 text-xs text-base-content/60")], [detail]),
			],
		);
	return h.section(
		[
			h.DataAttribute("testid", "home-cadences"),
			h.AriaLabel(heading),
			h.Class("flex flex-col gap-2"),
		],
		[
			h.details(
				[],
				[
					h.summary(
						[h.Class("cursor-pointer list-none")],
						[sectionHeading(h, heading)],
					),
					h.p(
						[h.Class("mb-2 text-sm text-base-content/60")],
						[i18next.t("home.cadences.hint")],
					),
					h.ul(
						[h.Class("flex flex-col")],
						[
							...tracked.map((row) =>
								item(
									row.id,
									row.name,
									row.nextDue === null
										? Cards.cadenceLabel(row.cadence)
										: `${Cards.cadenceLabel(row.cadence)} · ${i18next.t("home.cadences.next", { date: row.nextDue })}`,
								),
							),
							...week.paused.map((row) =>
								item(row.id, row.name, i18next.t("home.cadences.paused")),
							),
						],
					),
				],
			),
		],
	);
};

/** KTD1 flowchart: the feed replaces the results area; R14 collapses an empty circle to the week's empty state. */
const feedView = (h: HtmlBuilder<Message>, model: Model, shared: Shared) => {
	const week = AsyncData.getOrElse(model.keepInTouch, () => null);
	const recent = AsyncData.getOrElse(model.recent, () => null);
	return h.div(
		[h.Class("flex flex-col gap-10")],
		[
			briefHeader(h, model),
			keepInTouchStrip(h, model, shared),
			...(!isFeedSettled(model) || isEmptyCircle(model)
				? []
				: [
						captureBox(h, model, shared),
						h.div(
							[h.Class("grid gap-10 sm:grid-cols-2")],
							[
								week === null
									? h.empty
									: openTasksSection(h, shared, week.openTasks),
								recent === null || recent.length === 0
									? h.empty
									: recentSection(h, shared, recent),
							],
						),
						week === null ? h.empty : cadencesSection(h, shared, week),
					]),
		],
	);
};

export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const feed = isFeed(model, shared);
	const timeTraveling = Option.isSome(shared.asOf);
	const visible = visibleRows(model, shared);
	const all = rows(model);
	const selectedSet = new Set(model.selected);
	const selectedRows = all.filter((row) => selectedSet.has(row.id));
	const selectedRecords = selectedRows.filter(
		(row) => row.type === "node",
	).length;
	const allSelected = all.length > 0 && model.selected.length === all.length;
	const someSelected = model.selected.length > 0 && !allSelected;
	const nextCursor = AsyncData.getOrElse(model.results, () => ({
		nextCursor: Option.none<number>(),
	})).nextCursor;
	return h.div(
		[
			h.DataAttribute("testid", "page-Searched"),
			h.Class(
				"mx-auto min-h-screen max-w-[52.5rem] border-x border-base-300 bg-base-100 px-4 py-6 sm:px-8",
			),
		],
		[
			timeTraveling
				? h.empty
				: confirmDialog(h, {
						slotId: "confirm-delete",
						model: model.deleteDialog,
						toParentMessage: (message) =>
							Message.GotDeleteDialogMessage({ message }),
						title: t("dialog.confirmDelete.title"),
						message: `${deleteMessage(selectedRows)}${Option.match(
							model.deleteError,
							{
								onNone: () => "",
								onSome: (error) => ` — ${error}`,
							},
						)}`,
						confirmLabel: t("dialog.confirmDelete.confirm"),
						cancelLabel: t("dialog.confirmDelete.cancel"),
						onConfirm: Message.ClickedConfirmDelete(),
						isPending: model.pendingDeletes > 0,
					}),
			timeTraveling ? h.empty : addRecordDialog(h, model, shared),
			// The Home feed shows no search toolbar: the shell's Search (Cmd-K) is the one search field.
			feed
				? h.empty
				: h.div(
						[h.Class("sticky top-0 bg-base-100 z-10")],
						[
							h.div(
								[h.Class("relative")],
								[
									h.div(
										[
											h.Class(
												"mb-6 flex max-w-3xl flex-wrap items-center gap-2 mx-auto",
											),
										],
										[
											timeTraveling
												? h.empty
												: h.button(
														[
															h.Type("button"),
															h.Class("btn btn-ghost btn-square"),
															h.AriaLabel(t("search.addRecord")),
															h.OnClick(Message.ClickedAddRecord()),
														],
														[Plus(h)],
													),
											// R21: the import sits beside adding a record, always reachable.
											timeTraveling
												? h.empty
												: h.a(
														[
															h.Href(
																linkHref(
																	shared.route,
																	Route.Import({
																		asOf: Option.none(),
																		onboarding: Option.none(),
																	}),
																),
															),
															h.Class("btn btn-ghost btn-square"),
															h.AriaLabel(i18next.t("import.title")),
														],
														[Download(h)],
													),
											h.label(
												[
													h.Class(
														// Below sm the field takes its own row so a phone sees the whole placeholder.
														"input input-bordered order-first flex w-full items-center gap-2 rounded-full sm:order-none sm:w-auto sm:flex-1",
													),
												],
												[
													h.input([
														h.Id(SEARCH_INPUT_ID),
														h.Type("text"),
														h.Class("grow"),
														h.Placeholder(
															timeTraveling
																? t("search.filterPlaceholder")
																: t("search.searchPlaceholder"),
														),
														h.AriaLabel(
															timeTraveling
																? t("search.filterPlaceholder")
																: t("search.searchPlaceholder"),
														),
														h.Value(model.searchText),
														h.OnInput((value) =>
															Message.ChangedSearchText({ value }),
														),
													]),
													Search(h),
												],
											),
											timeTraveling
												? h.empty
												: h.label(
														[h.Class("select w-40")],
														[
															h.div(
																[h.Class("label")],
																[
																	h.span(
																		[h.Class("label-text text-xs")],
																		[t("search.type")],
																	),
																],
															),
															h.select(
																[
																	h.Class("select select-bordered select-sm"),
																	h.AriaLabel(t("search.type")),
																	h.Value(model.searchType),
																	h.OnChange((value) =>
																		Message.ChangedSearchType({
																			value:
																				SearchType.literals.find(
																					(t) => t === value,
																				) ?? "all",
																		}),
																	),
																],
																SearchType.literals.map((type) =>
																	h.option(
																		[
																			h.Value(type),
																			h.Selected(model.searchType === type),
																		],
																		[
																			type === "all"
																				? t("search.all")
																				: type === "node"
																					? t("search.record")
																					: t("search.entry"),
																		],
																	),
																),
															),
														],
													),
											!timeTraveling && model.searchType === "knot"
												? h.label(
														[h.Class("select w-48")],
														[
															h.div(
																[h.Class("label")],
																[
																	h.span(
																		[h.Class("label-text text-xs")],
																		[t("search.entrySubtype")],
																	),
																],
															),
															h.select(
																[
																	h.Class("select select-bordered select-sm"),
																	h.AriaLabel(t("search.entrySubtype")),
																	h.Value(model.knotType),
																	h.OnChange((value) =>
																		Message.ChangedEntryType({
																			value:
																				EntryFilter.literals.find(
																					(t) => t === value,
																				) ?? "all",
																		}),
																	),
																],
																[
																	h.option(
																		[
																			h.Value("all"),
																			h.Selected(model.knotType === "all"),
																		],
																		[t("search.all")],
																	),
																	...ENTRY_SUBTYPE_GROUPS.map(
																		([groupKey, types]) =>
																			h.optgroup(
																				[
																					h.Attribute(
																						"label",
																						i18next.t(groupKey),
																					),
																				],
																				types.map((type) =>
																					h.option(
																						[
																							h.Value(type),
																							h.Selected(
																								model.knotType === type,
																							),
																						],
																						[entrySubtypeLabel(type)],
																					),
																				),
																			),
																	),
																],
															),
														],
													)
												: h.empty,
										],
									),
								],
							),
							timeTraveling || feed
								? h.empty
								: h.div(
										[
											h.Class(
												"flex justify-start border-b pb-4 max-w-3xl mx-auto space-x-4 items-center",
											),
										],
										[
											h.input([
												h.Type("checkbox"),
												h.Class("checkbox checkbox-sm ml-3 mt-2 mb-2"),
												h.AriaLabel(t("search.selectAll")),
												h.Checked(allSelected),
												h.AriaChecked(someSelected ? "mixed" : allSelected),
												h.OnClick(
													Message.ToggledSelectAll({ selected: !allSelected }),
												),
											]),
											h.button(
												[
													h.Type("button"),
													h.Class("btn btn-sm"),
													h.AriaLabel(t("common.delete")),
													h.Disabled(model.selected.length === 0),
													h.OnClick(Message.ClickedDelete()),
												],
												[t("common.delete")],
											),
											h.button(
												[
													h.Type("button"),
													h.Class("btn btn-sm"),
													h.AriaLabel(t("search.merge")),
													h.Title(t("search.mergeHint")),
													h.Disabled(selectedRecords !== 2 || model.merging),
													h.OnClick(Message.ClickedMerge()),
												],
												[t("search.merge")],
											),
										],
									),
						],
					),
			h.div(
				[h.Class("max-w-3xl mx-auto")],
				[
					feed
						? feedView(h, model, shared)
						: AsyncData.isLoading(model.results)
							? h.div(
									[h.Role("status"), h.Class("w-full text-center")],
									[h.span([h.Class("loading loading-dots loading-lg")], [])],
								)
							: AsyncData.isFailure(model.results)
								? h.div(
										[h.Role("alert"), h.Class("alert alert-error")],
										[
											AlertCircle(h, {
												class: "h-6 w-6 shrink-0 stroke-current",
											}),
											h.span([], [t("search.loadFailed")]),
										],
									)
								: visible.length === 0
									? emptyState(h, model)
									: h.div(
											[],
											[
												h.div(
													[h.Class("max-w-3xl mx-auto")],
													visible.map((row) =>
														resultRow(
															h,
															shared,
															row,
															model.selected.includes(row.id),
														),
													),
												),
												AsyncData.isRefreshing(model.results)
													? h.div(
															[
																h.Role("status"),
																h.Class("w-full text-center mt-4"),
															],
															[
																h.span(
																	[h.Class("loading loading-dots loading-lg")],
																	[],
																),
															],
														)
													: Option.isSome(nextCursor)
														? h.div(
																[h.Class("w-full text-center mt-4")],
																[
																	h.button(
																		[
																			h.Type("button"),
																			h.Class("btn btn-sm"),
																			h.AriaLabel(t("search.loadMore")),
																			h.OnClick(Message.ClickedLoadMore()),
																		],
																		[t("search.loadMore")],
																	),
																],
															)
														: h.empty,
											],
										),
				],
			),
		],
	);
});
